import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Eye, EyeOff, Loader2, Church, School, Home, Users } from 'lucide-react';
import axios from 'axios';
import PasswordStrengthMeter, { isPasswordValid } from '@/components/PasswordStrengthMeter';

const API = process.env.REACT_APP_BACKEND_URL;

const OrganizationRegister = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    organization_name: '',
    organization_type: 'sunday_school',
    student_count: 25,
    contact_phone: ''
  });
  const [promoCode, setPromoCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const orgTypes = [
    { value: 'sunday_school', label: 'Sunday School / Church', icon: Church },
    { value: 'school', label: 'School / Academy', icon: School },
    { value: 'homeschool', label: 'Homeschool Co-op', icon: Home },
    { value: 'daycare', label: 'Daycare / Learning Center', icon: Users },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid(formData.password)) {
      toast.error('Password does not meet complexity requirements. Please satisfy every requirement below the field.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/auth/register-organization`, formData);
      const { access_token } = response.data;
      
      localStorage.setItem('littlelearners-token', access_token);
      toast.success('Organization registered! Sending you to secure checkout…');

      // Kick off per-seat Stripe checkout based on the student count already collected.
      try {
        const url = new URL(`${API}/api/subscriptions/organization-checkout`);
        url.searchParams.set('student_count', String(formData.student_count || 1));
        if (promoCode.trim()) url.searchParams.set('promo_code', promoCode.trim().toUpperCase());
        const checkout = await axios.post(
          url.toString(),
          {},
          { headers: { Authorization: `Bearer ${access_token}` } }
        );
        if (checkout.data?.url) {
          window.location.href = checkout.data.url;
          return;
        }
        // Fallback: no checkout URL returned — land in onboarding so the org can still set up.
        navigate('/organization/onboarding');
        window.location.reload();
      } catch (checkoutErr) {
        const msg = checkoutErr?.response?.data?.detail || checkoutErr?.message || 'Could not start checkout.';
        // eslint-disable-next-line no-console
        console.warn('Org checkout failed:', checkoutErr?.response?.data || checkoutErr);
        toast.error(`${msg}. You can enable billing later from Organization Settings.`);
        navigate('/organization/onboarding');
        window.location.reload();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <span className="font-fredoka text-2xl font-bold text-slate-800">Zazu</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-fredoka text-3xl text-slate-900">Organization Signup</CardTitle>
            <CardDescription className="text-base">Register your Sunday school, academy, or learning center</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    placeholder="Admin Name"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    required
                    data-testid="org-admin-name-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@organization.com"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    required
                    data-testid="org-email-input"
                    className="h-11"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization_name">Organization Name</Label>
                <Input
                  id="organization_name"
                  placeholder="First Baptist Sunday School"
                  value={formData.organization_name}
                  onChange={(e) => updateField('organization_name', e.target.value)}
                  required
                  data-testid="org-name-input"
                  className="h-11"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Organization Type</Label>
                  <Select
                    value={formData.organization_type}
                    onValueChange={(value) => updateField('organization_type', value)}
                  >
                    <SelectTrigger className="h-11" data-testid="org-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {orgTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="student_count">Expected Students</Label>
                  <Select
                    value={formData.student_count.toString()}
                    onValueChange={(value) => updateField('student_count', parseInt(value))}
                  >
                    <SelectTrigger className="h-11" data-testid="student-count-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">Up to 10</SelectItem>
                      <SelectItem value="25">Up to 25</SelectItem>
                      <SelectItem value="50">Up to 50</SelectItem>
                      <SelectItem value="100">Up to 100</SelectItem>
                      <SelectItem value="250">Up to 250</SelectItem>
                      <SelectItem value="500">500+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_phone">Phone (Optional)</Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.contact_phone}
                  onChange={(e) => updateField('contact_phone', e.target.value)}
                  data-testid="org-phone-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    required
                    data-testid="org-password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <PasswordStrengthMeter password={formData.password} />
              </div>

              {/* Optional promo code (applied at Stripe checkout) */}
              <div>
                <Label htmlFor="org-promo">Promo code (optional)</Label>
                <Input
                  id="org-promo"
                  data-testid="org-promo-input"
                  placeholder="e.g. FAMILY3, COPUSA"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  className="h-11 uppercase"
                />
                <p className="text-xs text-slate-500 mt-1">Applied on the next screen when you check out.</p>
              </div>

              {/* Live per-seat price estimate */}
              {(() => {
                const n = Number(formData.student_count) || 0;
                const perStudent = n >= 100 ? 1.5 : n >= 50 ? 2.0 : n >= 25 ? 2.5 : 3.0;
                const monthly = (perStudent * n).toFixed(2);
                return (
                  <div className="bg-purple-50 rounded-xl p-4 text-sm" data-testid="org-pricing-preview">
                    <p className="font-medium text-purple-800 mb-1">Your monthly total</p>
                    <p className="text-purple-800">
                      <span className="text-2xl font-bold">${monthly}</span>
                      <span className="text-xs ml-2">/ month · ${perStudent.toFixed(2)}/student × {n || 0} students</span>
                    </p>
                    <p className="text-xs text-purple-600 mt-2">
                      After you register, we'll send you to secure Stripe checkout. Once payment is confirmed,
                      you'll continue with classroom setup and teacher invites.
                    </p>
                  </div>
                );
              })()}

              <Button
                type="submit"
                className="w-full h-12 text-lg bg-purple-600 hover:bg-purple-700"
                disabled={loading}
                data-testid="org-register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  'Register Organization'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-600">
                Individual parent account?{' '}
                <Link to="/register" className="text-sky-500 hover:text-sky-600 font-semibold">
                  Sign up here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default OrganizationRegister;
